import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { theme } from "@Styled";
import {
    StyledFooterWrapper,
    StyledActions,
    StyledActionsLeft,
    CommentsContainer,
    UndoCountdownWrapper,
    UndoCountdownText,
    UndoCountdownChip,
    StyledActionItem,
} from "./PollItemFooter.styled";
import ActionButton from "@Components/ActionButton/ActionButton.component";
import {
    CommentsIcon,
    VotesIcon,
    ShareIcon,
    RefreshIcon,
} from "@Components/Icons";
import PollItemComments from "../PollItemComments/PollItemComments";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { PollActions, RootStateType, ChannelActions } from "@Store";
import { useTranslation } from "react-i18next";
import GuestCommentForm from "../PollItemComments/GuestCommentForm";

interface IPollItemFooter {
    total_votes: number;
    comments: {
        count: number;
        list: any[]; // Simplified for readability
        isLoading: boolean;
        isExpanded: boolean;
        loadMore: boolean;
    };
    likes: string[];
    hasUserVoted: boolean;
    pollId: string;
    authorId: string;
    channelId: string;
    disabledShare: boolean;
    channel?: any; // Simplified for readability
    undoCountdownEndsAt?: number | null;
    isGuestViewer: boolean;
    hideResultsBeforeVote: boolean;
    allowComments?: boolean;
}

const PollItemFooter: React.FC<IPollItemFooter> = ({
    total_votes,
    comments,
    hasUserVoted,
    pollId,
    channelId,
    authorId,
    undoCountdownEndsAt,
    isGuestViewer,
    hideResultsBeforeVote,
    allowComments = true,
}) => {
    const { t } = useTranslation(); // Generate a random guest name between 1 and 999
    const generateRandomGuestName = () =>
        `Guest${Math.floor(Math.random() * 999) + 1}`;
    const [isCommentsExpanded, setCommentsExpanded] = useState(false); // State to track expanded comments
    const [body, setBody] = useState("");
    const [username, setUsername] = useState(generateRandomGuestName);
    const currentUser = useSelector((state: RootStateType) => state.auth);
    const userId = currentUser.user.id;
    const isPollOwner = Boolean(userId) && authorId === userId;
    const [hasVoted, setHasVoted] = useState(
        !!localStorage.getItem(`poll_${pollId}_hasVoted`)
    );
    const [secondsLeft, setSecondsLeft] = useState(0);

    const dispatch = useDispatch<any>();

    const commentsEnabled = allowComments || isPollOwner;

    // Toggle the visibility of comments
    const toggleExpandComments = () => {
        if (!commentsEnabled) return;
        setCommentsExpanded((prev) => !prev);
    };

    // Handle sharing the poll
    const handleSharePoll = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.SharedLinkDialog,
                props: {
                    link: `${window.location.protocol}//${window.location.host}/single?pid=${pollId}`,
                    title: "Poll",
                    openDialog: true,
                },
            })
        );
    };

    const handleRefreshPoll = useCallback(() => {
        if (!userId || !pollId) return;
        // Refresh both the single poll and the channel list entry so the NPS card stays in sync.
        dispatch(PollActions.fetchSinglePoll(pollId));
        if (channelId) {
            dispatch(ChannelActions.fetchChannelPolls(channelId));
        }
    }, [userId, pollId, channelId, dispatch]);

    const handleCommentChange = (payload: {
        body: string;
        username?: string;
    }) => {
        setUsername(payload.username ?? "");
        setBody(payload.body);
    };

    const handleSubmit = () => {
        if (!allowComments) return;
        if (!body || !username) return;
        const commentPayload = { body, username };
        dispatch(PollActions.addCommentInPoll(pollId, commentPayload));
        setBody("");
        setCommentsExpanded(false);

        localStorage.setItem(`poll_${pollId}_hasVoted`, "true");
        setHasVoted(true);
    };

    // Show countdown only when timer exists and seconds remaining > 0
    const isCountdownVisible = useMemo(
        () => Boolean(undoCountdownEndsAt) && secondsLeft > 0,
        [undoCountdownEndsAt, secondsLeft]
    );

    useEffect(() => {
        if (!undoCountdownEndsAt) {
            setSecondsLeft(0);
            return;
        }

        const updateSeconds = () => {
            const msLeft = undoCountdownEndsAt - Date.now();
            setSecondsLeft(msLeft > 0 ? Math.ceil(msLeft / 1000) : 0);
        };

        updateSeconds();

        const intervalId = setInterval(updateSeconds, 250);

        return () => clearInterval(intervalId);
    }, [undoCountdownEndsAt]);

    const shouldHideVoteSection =
        isGuestViewer && hideResultsBeforeVote && !isPollOwner;
    const voteCountValue = shouldHideVoteSection ? undefined : total_votes;

    return (
        <>
            <StyledFooterWrapper>
                <StyledActions>
                    <StyledActionsLeft>
                        {/* When countdown is active, show countdown prominently and hide the comments button
                            to avoid layout churn and horizontal overflow on small screens. */}
                        {isCountdownVisible ? (
                            <StyledActionItem>
                                <UndoCountdownWrapper>
                                    <UndoCountdownText>
                                        {t("poll_item.undo_vote_timer", {
                                            countdown: secondsLeft,
                                            defaultValue:
                                                "Change vote in {{countdown}}s",
                                        })}
                                    </UndoCountdownText>
                                    <UndoCountdownChip
                                        aria-hidden="true"
                                        label={t(
                                            "poll_item.vote_submit_countdown",
                                            {
                                                defaultValue:
                                                    "Submitting your vote…",
                                            }
                                        )}
                                        avatar={<Avatar>{secondsLeft}</Avatar>}
                                    />
                                </UndoCountdownWrapper>
                            </StyledActionItem>
                        ) : (
                            commentsEnabled && (
                                <li>
                                    <ActionButton
                                        icon={<CommentsIcon />}
                                        value={
                                            isPollOwner
                                                ? comments.count
                                                : undefined
                                        }
                                        label={
                                            isGuestViewer
                                                ? t("poll_item.leave_comment")
                                                : undefined
                                        }
                                        onClick={toggleExpandComments}
                                    />
                                </li>
                            )
                        )}
                        <li>
                            {isPollOwner && (
                                <ActionButton
                                    icon={<RefreshIcon />}
                                    onClick={handleRefreshPoll}
                                />
                            )}
                        </li>
                        {isPollOwner && (
                            <li>
                                <ActionButton
                                    icon={<ShareIcon />}
                                    onClick={handleSharePoll}
                                />
                            </li>
                        )}
                    </StyledActionsLeft>
                    {/* Right side: votes (only when not hidden). Keep min-width:0 to avoid overflow. */}
                    {!shouldHideVoteSection && (
                        <StyledActionItem>
                            <ActionButton
                                icon={
                                    <VotesIcon
                                        color={
                                            hasUserVoted
                                                ? theme.palette.common.blue
                                                : theme.palette.common.gray
                                        }
                                    />
                                }
                                value={voteCountValue}
                                label={t("poll_item.votes")}
                                noClickable
                            />
                        </StyledActionItem>
                    )}
                </StyledActions>
            </StyledFooterWrapper>
            {commentsEnabled && (
                <CommentsContainer isExpanded={isCommentsExpanded}>
                    {isPollOwner ? (
                        <PollItemComments
                            pollId={pollId}
                            channelId={channelId}
                            commentsList={comments}
                        />
                    ) : (
                        <GuestCommentForm
                            username={username}
                            body={body}
                            onCommentChange={handleCommentChange}
                            onSubmit={handleSubmit}
                            hasVoted={hasVoted}
                        />
                    )}
                </CommentsContainer>
            )}
        </>
    );
};

export default PollItemFooter;
